'use client';

import * as React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import 'dayjs/locale/en';
import 'dayjs/locale/ja';
import 'dayjs/locale/ko';
import 'dayjs/locale/vi';
import 'dayjs/locale/zh-cn';
import { MDXRemote } from 'next-mdx-remote';
import { useTranslations, useLocale } from 'next-intl';
import { Box, Breadcrumbs, Divider, Stack, Typography } from '@unity/cloud-ui';
import { useEffect } from 'react';
import { usePathname } from '@/i18n/routing';
import { Footer } from '@/components/footer';
import type { Content } from '@/lib/content-loader';
import { LAST_VISITED_PATH_KEY } from '@/lib/constants/session-storage';
import themeConstants from '@/components/theme/theme-constants';
import { ThemeContext } from '@/components/theme';
import { LOCALE_TO_DAYJS_LOCALE } from '@/i18n';
import { Link } from '@/components/content-renderer/mdx-components/link';
import { ContentRefresh } from './content-refresh';
import mdxComponents from './mdx-components';

dayjs.extend(duration);
dayjs.extend(relativeTime);

export const ContentRenderer = (props: Content) => {
  const t = useTranslations();
  const locale = useLocale();
  const pathname = usePathname();
  const { zoom } = React.useContext(ThemeContext);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { readingTime, tableOfContents, breadcrumbs, ...mdxProps } = props;
  const { title, description, lastModifiedDate } = props.frontmatter as {
    title: string;
    description: string;
    lastModifiedDate?: string;
  };

  useEffect(() => {
    sessionStorage.setItem(LAST_VISITED_PATH_KEY, pathname);
  }, [pathname]);

  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a');

      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('#')) return;
      const id = href.slice(1);
      const tocLink = document.getElementById(id)?.querySelector('a');
      tocLink?.click();
    };

    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);

  const renderBreadcrumbs = () => {
    return (
      breadcrumbs.length > 0 && (
        <Breadcrumbs itemsBeforeCollapse={0} itemsAfterCollapse={2} aria-label="breadcrumb">
          {breadcrumbs.map((breadcrumb) => (
            <Link
              key={breadcrumb.title}
              href={breadcrumb.url}
              sx={{
                color: ({ vars }) => vars.palette.text.primary,
              }}
            >
              {breadcrumb.title}
            </Link>
          ))}
        </Breadcrumbs>
      )
    );
  };

  return (
    <ContentRefresh>
      <Box
        display="flex"
        justifyContent="center"
        sx={{
          minHeight: (theme) =>
            `calc(100svh - ${themeConstants.layout.headerHeight} - ${themeConstants.layout.productHeaderHeight} - ${theme.spacing(4)})`,
        }}
      >
        <Stack
          sx={(theme) => ({
            zoom: themeConstants.zoom[zoom],
            [theme.breakpoints.down('lg')]: {
              width: '100%',
              pt: theme.spacing(4),
              px: theme.spacing(4),
            },
            [theme.breakpoints.up('lg')]: {
              maxWidth: '780px',
              pt: theme.spacing(4),
            },
            '.MuiTypography-body1': {
              lineHeight: '1.5rem',
            },
            h1: {
              fontSize: '32px',
              lineHeight: '40px',
              letterSpacing: '-0.01rem',
            },
            h2: {
              fontSize: '19px',
              lineHeight: '23px',
              letterSpacing: '-0.019rem',
            },
          })}
        >
          <Stack gap={themeConstants.layout.pageContentSpacing} flexGrow={1} mb={5} id="page-content-container">
            {renderBreadcrumbs()}
            <Typography variant="h1" sx={{ wordWrap: 'break-word' }}>
              {title}
            </Typography>
            <Typography
              variant="h4"
              component="div"
              sx={{ fontWeight: 400, fontSize: '1rem', lineHeight: '1.5rem', color: 'text.secondary' }}
            >
              {description}
            </Typography>
            <Box display="flex" gap={1} color={'text.secondary'}>
              <Typography variant="caption">{t('content-renderer:read-time', { readingTime })}</Typography>
              {lastModifiedDate && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Typography variant="caption">
                    {t('content-renderer:last-updated', {
                      lastUpdatedTime: dayjs(lastModifiedDate)
                        .locale(LOCALE_TO_DAYJS_LOCALE[locale] ?? 'en')
                        .fromNow(),
                    })}
                  </Typography>
                </>
              )}
            </Box>
            <Divider />
            <MDXRemote {...mdxProps} components={mdxComponents} />
          </Stack>
          <Divider />
          <Footer />
        </Stack>
      </Box>
    </ContentRefresh>
  );
};
